import { makeObservable, observable } from 'mobx';

import { getWorkspaceContents, WORKSPACE_OBJECT_TYPES, type WorkspaceItem, type WorkspaceParent } from 'api/workspace/endpoints/getWorkspaceContents';
import { extractErrorMessage } from 'helpers/extractErrorMessage';
import { acceptDegraded } from 'validation';

const PAGE_LIMIT = 50;

/**
 * The pane's state as one value, so the display and the in-flight activity can
 * never drift apart. `loadingMore` / `refreshing` / `backgroundError` are fields
 * of `loaded` — not peer states — because they modify a visible list rather than
 * replace it. Making them unreachable outside `loaded` is what keeps a spinner
 * flag from outliving the list it belonged to: any transition away from `loaded`
 * drops them atomically.
 */
type ContentsState =
  | { readonly status: 'empty' } // before the first selection
  | { readonly status: 'loading' } // first page; blocking; no rows yet
  | { readonly status: 'error'; readonly message: string } // first-page load failed; no rows
  | {
      readonly status: 'loaded';
      readonly items: readonly WorkspaceItem[];
      readonly total: number;
      readonly nextCursor: string | null;
      readonly loadingMore: boolean; // appending the next keyset page
      readonly refreshing: boolean; // silent refetch of page 1 in flight
      readonly backgroundError: string | null; // a failed loadMore/refresh; rows kept
    };

/**
 * A latest-wins re-entrancy guard shared by every fetch in the model. Each `run`
 * bumps one epoch and hands the body an `isCurrent()` token; the body commits to
 * state only while current, so a superseded fetch (a newer load, a silent
 * refresh, a container switch) can't clobber the visible state. Because the
 * epoch is shared, a first-page fetch also invalidates an in-flight `loadMore` —
 * the two can't interleave onto a stale cursor.
 */
const latestOnly = () => {
  let epoch = 0;
  return <R>(body: (isCurrent: () => boolean) => Promise<R>): Promise<R> => {
    const mine = ++epoch;
    return body(() => mine === epoch);
  };
};

/**
 * The merged-contents pane's state for the selected container. Owns the loaded
 * page window (keyset-appended via `loadMore`), the overall total, and load/
 * error flags. Fetches into local state — no redux slice (issue #887). Pure
 * observer views read the getters; all logic is unit-testable without React.
 */
export class WorkspaceContentsModel {
  private _state: ContentsState = { status: 'empty' };
  private _parent: WorkspaceParent | null = null;
  private readonly _run = latestOnly();

  constructor() {
    makeObservable<WorkspaceContentsModel, '_state' | '_parent'>(this, {
      _state: observable.ref,
      _parent: observable.ref,
    });
  }

  /** A failed load: the first-page error, or a kept-rows loadMore/refresh error. */
  get error(): string | null {
    const s = this._state;
    if (s.status === 'error') return s.message;
    if (s.status === 'loaded') return s.backgroundError;
    return null;
  }

  /** Whether a further page exists (a non-null keyset cursor). */
  get hasMore(): boolean {
    const s = this._state;
    return s.status === 'loaded' && s.nextCursor !== null;
  }

  get items(): readonly WorkspaceItem[] {
    const s = this._state;
    return s.status === 'loaded' ? s.items : [];
  }

  get loading(): boolean {
    return this._state.status === 'loading';
  }

  get loadingMore(): boolean {
    const s = this._state;
    return s.status === 'loaded' && s.loadingMore;
  }

  /** The container currently shown (null before the first selection). */
  get parent(): WorkspaceParent | null {
    return this._parent;
  }

  get total(): number {
    const s = this._state;
    return s.status === 'loaded' ? s.total : 0;
  }

  /** Replace the pane with the first page of `parent`'s contents. */
  load = (parent: WorkspaceParent): Promise<void> => this.fetchFirstPage(parent, false);

  /** Append the next keyset page. No-op when there is no next page or a fetch is in flight. */
  loadMore = (): Promise<void> => {
    const parent = this._parent;
    const s = this._state;
    // Only page from a quiescent `loaded`. While any first-page fetch is running
    // the state is `loading` or `loaded{refreshing}`, so this early-return also
    // keeps `loadMore` from paging (or bumping the shared epoch) mid-refresh.
    if (parent === null || s.status !== 'loaded' || s.nextCursor === null || s.loadingMore || s.refreshing) return Promise.resolve();
    const cursor = s.nextCursor;
    return this._run(async (isCurrent) => {
      this._state = { ...s, loadingMore: true, backgroundError: null };
      try {
        const result = acceptDegraded(await getWorkspaceContents({ parent, types: WORKSPACE_OBJECT_TYPES, cursor, limit: PAGE_LIMIT }));
        if (!isCurrent()) return;
        const cur = this._state;
        if (cur.status !== 'loaded') return;
        this._state = { ...cur, items: [...cur.items, ...result.items], total: result.total, nextCursor: result.nextCursor, loadingMore: false };
      } catch (err) {
        if (!isCurrent()) return;
        const cur = this._state;
        if (cur.status !== 'loaded') return;
        this._state = { ...cur, loadingMore: false, backgroundError: extractErrorMessage(err, 'Failed to load more contents') };
      }
    });
  };

  /** Silently refetch the current container's first page (after a create/edit/delete). */
  refresh = (): void => {
    const parent = this._parent;
    if (parent !== null) void this.fetchFirstPage(parent, true);
  };

  /**
   * Load `parent`'s first page. A non-silent load shows the blocking spinner
   * (drops the current rows); a silent refresh keeps the current rows visible
   * (marking `refreshing`) when there are rows to keep, else falls back to the
   * blocking spinner since there is nothing to preserve. Either way any in-flight
   * `loadMore` is invalidated (shared epoch) and `loadingMore` is dropped.
   */
  private fetchFirstPage = (parent: WorkspaceParent, silent: boolean): Promise<void> =>
    this._run(async (isCurrent) => {
      this._parent = parent;
      const prev = this._state;
      if (silent && prev.status === 'loaded') {
        this._state = { ...prev, loadingMore: false, backgroundError: null, refreshing: true };
      } else {
        this._state = { status: 'loading' };
      }
      try {
        const result = acceptDegraded(await getWorkspaceContents({ parent, types: WORKSPACE_OBJECT_TYPES, limit: PAGE_LIMIT }));
        if (!isCurrent()) return;
        this._state = {
          status: 'loaded',
          items: result.items,
          total: result.total,
          nextCursor: result.nextCursor,
          loadingMore: false,
          refreshing: false,
          backgroundError: null,
        };
      } catch (err) {
        if (!isCurrent()) return;
        const message = extractErrorMessage(err, 'Failed to load contents');
        const cur = this._state;
        // A failed silent refresh keeps the rows it was refreshing and surfaces
        // the error non-destructively; a failed blocking load has no rows to keep.
        if (silent && cur.status === 'loaded') {
          this._state = { ...cur, refreshing: false, backgroundError: message };
        } else {
          this._state = { status: 'error', message };
        }
      }
    });
}
